<template>
  <div class="kw">
    <ul v-if="modelValue.length" class="kw__chips">
      <li v-for="(kw, i) in modelValue" :key="i" class="kw__chip">
        <span class="kw__chip-text">{{ toDisplay(kw) }}</span>
        <StIconButton
          class="kw__chip-x"
          icon="xmark"
          :label="`Remove ${toDisplay(kw)}`"
          size="sm"
          variant="danger"
          @click="removeAt(i)"
        />
      </li>
    </ul>

    <!-- Until "+ Keyword" is pressed there's no text box; pressing it reveals a
         focused input. The user only ever types/sees plain keywords — the
         **^^…^^** formatting is applied on store and stripped on display. -->
    <div class="kw__add">
      <StSuggestInput
        v-if="adding"
        ref="inputEl"
        v-model="draft"
        :placeholder="placeholder"
        :suggestions="suggestions"
        :exclude="usedKeywords"
        @select="selectSuggestion"
        @forget="forget"
        @enter="commitDraft"
        @keydown="onKeydown"
        @blur="onBlur"
      />
      <StButton size="sm" variant="secondary" icon="plus" @click="startAdding">
        {{ noun }}
      </StButton>
    </div>
  </div>
</template>

<script setup lang="ts">
// Tag-style keyword editor. The model is a string[] stored with the app's keyword
// formatting (`**^^KEYWORD^^**`); this component translates so the user only deals
// with plain text: chips show the unformatted keyword, and typed input is wrapped
// before it's emitted. Commas and Enter both commit (a comma splits into several).
import { computed, nextTick, onMounted, ref } from 'vue';
import { StSuggestInput, StButton, StIconButton } from './ui/index.js';
import { useKeywordSuggestions, type KeywordScope } from '../composables/useKeywordSuggestions.js';

const props = withDefaults(
  defineProps<{
    modelValue: string[];
    placeholder?: string;
    // Raw mode stores keywords as plain strings instead of the app's
    // `**^^…^^**` ability-keyword formatting. Use it for data that the app
    // looks up or filters by exact name (unit keywords, weapon abilities).
    raw?: boolean;
    // Uppercase keywords on store (unit keywords are uppercase by convention).
    uppercase?: boolean;
    // Label for the add button (e.g. "Keyword", "Ability").
    noun?: string;
    // Which suggestion pool to draw from / contribute to. Keeps the distinct
    // vocabularies (unit keywords, ability keywords, weapon abilities, army
    // names) from cross-contaminating each other.
    suggestionScope?: KeywordScope;
  }>(),
  {
    placeholder: 'Type a keyword…',
    raw: false,
    uppercase: false,
    noun: 'Keyword',
    suggestionScope: 'ability',
  },
);

const emit = defineEmits<{ (e: 'update:modelValue', value: string[]): void }>();

const draft = ref('');
const adding = ref(false);
const inputEl = ref<InstanceType<typeof StSuggestInput> | null>(null);

const { suggestions, record, forget } = useKeywordSuggestions(props.suggestionScope);

// Keywords already on this field shouldn't be offered again.
const usedKeywords = computed(() => props.modelValue.map((k) => toDisplay(k)));

// Remember keywords loaded into this field so they're suggestible immediately.
onMounted(() => {
  for (const k of props.modelValue) record(toDisplay(k));
});

// Strip the app's keyword formatting (`**^^…^^**`, or bare `^^…^^`) for display.
function toDisplay(stored: string): string {
  if (props.raw) return stored.trim();
  return stored
    .replace(/\*\*\^\^/g, '')
    .replace(/\^\^\*\*/g, '')
    .replace(/\^\^/g, '')
    .replace(/\*\*/g, '')
    .trim();
}

// Apply the app's keyword formatting when storing a plain keyword.
function toStored(display: string): string {
  const trimmed = props.uppercase ? display.trim().toUpperCase() : display.trim();
  return props.raw ? trimmed : `**^^${trimmed}^^**`;
}

async function startAdding() {
  // If already typing, commit the in-progress keyword first (acts like a + click).
  if (adding.value) {
    commitDraft();
    return;
  }
  adding.value = true;
  await nextTick();
  inputEl.value?.focus();
}

function addKeywords(raw: string) {
  const parts = raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (!parts.length) return;
  const seen = new Set(props.modelValue.map((k) => toDisplay(k).toLowerCase()));
  const next = [...props.modelValue];
  for (const p of parts) {
    if (!seen.has(p.toLowerCase())) {
      next.push(toStored(p));
      seen.add(p.toLowerCase());
    }
    // Remember every typed keyword (plain form), even duplicates of existing chips.
    record(p);
  }
  if (next.length !== props.modelValue.length) emit('update:modelValue', next);
}

// Picking a suggestion adds it as a chip and clears the draft for the next entry.
function selectSuggestion(keyword: string) {
  addKeywords(keyword);
  draft.value = '';
  inputEl.value?.focus();
}

function commitDraft() {
  addKeywords(draft.value);
  draft.value = '';
  // Keep the box open and focused for rapid entry of several keywords.
  inputEl.value?.focus();
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === ',') {
    e.preventDefault();
    commitDraft();
  } else if (e.key === 'Escape') {
    draft.value = '';
    adding.value = false;
  } else if (e.key === 'Backspace' && !draft.value && props.modelValue.length) {
    removeAt(props.modelValue.length - 1);
  }
}

// Blur closes the box (committing anything typed); the "+ Keyword" button brings
// it back. This keeps the resting state to just the button.
function onBlur() {
  if (draft.value.trim()) commitDraft();
  draft.value = '';
  adding.value = false;
}

function removeAt(i: number) {
  emit(
    'update:modelValue',
    props.modelValue.filter((_, idx) => idx !== i),
  );
}
</script>

<style scoped>
.kw {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-2);
}

.kw__chips {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: var(--st-space-1);
}

.kw__chip {
  display: inline-flex;
  align-items: center;
  gap: var(--st-space-1);
  padding: 2px var(--st-space-1) 2px var(--st-space-2);
  background: var(--st-surface-sunken);
  border: 1px solid var(--st-border);
  border-radius: var(--st-radius-full);
  font-size: var(--st-text-sm);
  color: var(--st-text);
}

.kw__chip-x {
  width: 1.1rem;
  height: 1.1rem;
  border-radius: var(--st-radius-full);
  font-size: var(--st-text-xs);
}

.kw__add {
  display: flex;
  align-items: center;
  gap: var(--st-space-2);
}
.kw__add :deep(.st-control) {
  flex: 1;
}
</style>
